import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function RevenueTrendsChart({ invoices, chartType = 'line' }) {
  const chartData = useMemo(() => {
    if (!invoices || invoices.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    // Get last 12 months
    const months = [];
    const currentDate = new Date();
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      months.push({
        label: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        year: date.getFullYear(),
        month: date.getMonth()
      });
    }

    // Calculate revenue by month
    const revenueData = months.map(month => {
      const monthInvoices = invoices.filter(invoice => {
        let invoiceDate;
        
        // Handle different date formats
        if (invoice.date?.seconds) {
          invoiceDate = new Date(invoice.date.seconds * 1000);
        } else if (invoice.createdAt?.seconds) {
          invoiceDate = new Date(invoice.createdAt.seconds * 1000);
        } else if (invoice.date) {
          invoiceDate = new Date(invoice.date);
        } else if (invoice.createdAt) {
          invoiceDate = new Date(invoice.createdAt);
        } else {
          return false;
        }
        
        console.log(`Invoice ${invoice.invoiceNumber}: Date=${invoiceDate.toISOString()}, Month=${month.label}, Status=${invoice.status}, Total=${invoice.total}`);
        
        return invoiceDate.getFullYear() === month.year && 
               invoiceDate.getMonth() === month.month;
      });

      const paidRevenue = monthInvoices
        .filter(inv => inv.status === 'paid')
        .reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0);

      const pendingRevenue = monthInvoices
        .filter(inv => inv.status === 'unpaid')
        .reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0);

      console.log(`${month.label}: Paid=${paidRevenue}, Pending=${pendingRevenue}, Total invoices=${monthInvoices.length}`);

      return { paidRevenue, pendingRevenue };
    });

    return {
      labels: months.map(m => m.label),
      datasets: [
        {
          label: 'Paid Revenue',
          data: revenueData.map(d => d.paidRevenue),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: chartType === 'line',
          tension: 0.4,
        },
        {
          label: 'Pending Revenue',
          data: revenueData.map(d => d.pendingRevenue),
          borderColor: 'rgb(249, 115, 22)',
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          fill: chartType === 'line',
          tension: 0.4,
        }
      ]
    };
  }, [invoices, chartType]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Revenue Trends (Last 12 Months)',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: £${context.parsed.y.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Month'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Revenue (£)'
        },
        ticks: {
          callback: function(value) {
            return '£' + value.toFixed(0);
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  if (chartData.labels.length === 0) {
    return (
      <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-2">📊</div>
          <p className="text-gray-500">No revenue data available</p>
          <p className="text-sm text-gray-400">Create some invoices to see trends</p>
        </div>
      </div>
    );
  }

  const ChartComponent = chartType === 'bar' ? Bar : Line;

  return (
    <div className="h-64 relative">
      <ChartComponent data={chartData} options={options} />
    </div>
  );
}
